import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  ConfirmDialog,
  ResultDialog,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  SearchableSelect,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { companyApi } from "@/services/api";
import {
  Plus,
  Landmark,
  Loader2,
  X,
  Trash2,
  MoreHorizontal,
  CheckCircle,
  Clock,
} from "lucide-react";

interface BankAccount {
  id: string;
  bankCode: string;
  bankName: string;
  agency: string;
  accountNumber: string;
  accountDigit?: string;
  accountType: string;
  holderName: string;
  holderDocument: string;
  pixKey?: string;
  pixKeyType?: string;
  isVerified: boolean;
  isActive: boolean;
  createdAt: string;
}

interface CompanyInfo {
  id: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string | null;
}

interface BankAccountForm {
  bankCode: string;
  bankName: string;
  agency: string;
  accountNumber: string;
  accountDigit: string;
  accountType: "CHECKING" | "SAVINGS";
  holderName: string;
  holderDocument: string;
  pixKey: string;
  pixKeyType: "cnpj" | "";
}

// Labels amigáveis para tipos de chave PIX
function getPixKeyTypeLabel(type: string): string {
  switch (type) {
    case "cnpj":
      return "CNPJ";
    case "cpf":
      return "CPF";
    case "email":
      return "E-mail";
    case "phone":
      return "Telefone";
    case "evp":
      return "Chave Aleatória";
    default:
      return type;
  }
}

// Lista completa dos principais bancos brasileiros
const BANK_LIST = [
  { code: "001", name: "Banco do Brasil" },
  { code: "003", name: "Banco da Amazônia" },
  { code: "004", name: "Banco do Nordeste" },
  { code: "021", name: "Banestes" },
  { code: "025", name: "Banco Alfa" },
  { code: "033", name: "Santander" },
  { code: "036", name: "Banco Bradesco BBI" },
  { code: "037", name: "Banco do Estado do Pará" },
  { code: "041", name: "Banrisul" },
  { code: "047", name: "Banco do Estado de Sergipe" },
  { code: "062", name: "Hipercard" },
  { code: "066", name: "Banco Morgan Stanley" },
  { code: "069", name: "Crefisa" },
  { code: "070", name: "BRB - Banco de Brasília" },
  { code: "074", name: "Banco J. Safra" },
  { code: "077", name: "Banco Inter" },
  { code: "079", name: "Banco Original do Agronegócio" },
  { code: "082", name: "Banco Topazio" },
  { code: "083", name: "Banco da China Brasil" },
  { code: "084", name: "Uniprime Norte do Paraná" },
  { code: "085", name: "Cooperativa Central de Crédito - Ailos" },
  { code: "089", name: "Credisan" },
  { code: "091", name: "Unicred Central RS" },
  { code: "093", name: "Polocred" },
  { code: "094", name: "Banco Finaxis" },
  { code: "096", name: "Banco B3" },
  { code: "097", name: "Credisis" },
  { code: "099", name: "Uniprime Central" },
  { code: "102", name: "XP Investimentos" },
  { code: "104", name: "Caixa Econômica Federal" },
  { code: "107", name: "Banco Bocom BBM" },
  { code: "119", name: "Banco Western Union" },
  { code: "120", name: "Banco Rodobens" },
  { code: "121", name: "Banco Agibank" },
  { code: "125", name: "Banco Genial" },
  { code: "126", name: "BR Partners" },
  { code: "127", name: "Codepe" },
  { code: "128", name: "MS Bank" },
  { code: "129", name: "UBS Brasil" },
  { code: "130", name: "Caruana" },
  { code: "131", name: "Tullett Prebon Brasil" },
  { code: "132", name: "ICBC do Brasil" },
  { code: "133", name: "Cresol Confederação" },
  { code: "134", name: "BGC Liquidez" },
  { code: "136", name: "Unicred" },
  { code: "138", name: "Get Money" },
  { code: "139", name: "Intesa Sanpaolo Brasil" },
  { code: "140", name: "Easynvest" },
  { code: "142", name: "Broker Brasil" },
  { code: "143", name: "Treviso" },
  { code: "144", name: "Bexs Banco de Câmbio" },
  { code: "145", name: "Levycam" },
  { code: "146", name: "Guitta" },
  { code: "149", name: "Facta" },
  { code: "157", name: "ICAP do Brasil" },
  { code: "159", name: "Casa do Crédito" },
  { code: "163", name: "Commerzbank Brasil" },
  { code: "169", name: "Banco Olé Bonsucesso Consignado" },
  { code: "173", name: "BRL Trust" },
  { code: "174", name: "Pernambucanas Financiadora" },
  { code: "177", name: "Guide" },
  { code: "180", name: "CM Capital Markets" },
  { code: "183", name: "Socred" },
  { code: "184", name: "Banco Itaú BBA" },
  { code: "188", name: "Ativa Investimentos" },
  { code: "189", name: "HS Financeira" },
  { code: "190", name: "Servicoop" },
  { code: "191", name: "Nova Futura" },
  { code: "194", name: "Parmetal" },
  { code: "196", name: "Fair" },
  { code: "197", name: "Stone" },
  { code: "208", name: "BTG Pactual" },
  { code: "212", name: "Banco Original" },
  { code: "213", name: "Banco Arbi" },
  { code: "217", name: "Banco John Deere" },
  { code: "218", name: "Banco BS2" },
  { code: "222", name: "Banco Credit Agricole" },
  { code: "224", name: "Banco Fibra" },
  { code: "233", name: "Banco Cifra" },
  { code: "237", name: "Bradesco" },
  { code: "241", name: "Banco Clássico" },
  { code: "243", name: "Banco Máxima" },
  { code: "246", name: "Banco ABC Brasil" },
  { code: "249", name: "Banco Investcred Unibanco" },
  { code: "250", name: "BCV - Banco de Crédito e Varejo" },
  { code: "253", name: "Bexs Corretora de Câmbio" },
  { code: "254", name: "Paraná Banco" },
  { code: "260", name: "Nubank" },
  { code: "265", name: "Banco Fator" },
  { code: "266", name: "Banco Cédula" },
  { code: "268", name: "Barigui" },
  { code: "269", name: "HSBC Brasil" },
  { code: "270", name: "Sagitur" },
  { code: "271", name: "IB Cctvm" },
  { code: "272", name: "AGK" },
  { code: "273", name: "Cooperativa de Crédito Rural de São Miguel do Oeste" },
  { code: "274", name: "Money Plus" },
  { code: "276", name: "Senff" },
  { code: "278", name: "Genial Investimentos" },
  { code: "279", name: "Cooperativa de Crédito Rural de Primavera do Leste" },
  { code: "280", name: "Avista" },
  { code: "281", name: "Cooperativa de Crédito Rural Coopavel" },
  { code: "283", name: "RB Capital Investimentos" },
  { code: "285", name: "Frente" },
  { code: "286", name: "Cooperativa de Crédito Rural de Ouro" },
  { code: "288", name: "Carol" },
  { code: "289", name: "Decyseo" },
  { code: "290", name: "PagSeguro" },
  { code: "292", name: "BS2" },
  { code: "293", name: "Lastro RDV" },
  { code: "296", name: "Vision" },
  { code: "298", name: "Vips" },
  { code: "299", name: "Sorocred" },
  { code: "300", name: "Banco de la Nacion Argentina" },
  { code: "301", name: "BPP Instituição de Pagamento" },
  { code: "306", name: "Portopar" },
  { code: "307", name: "Terra Investimentos" },
  { code: "309", name: "Cambionet" },
  { code: "310", name: "Vortx" },
  { code: "311", name: "Dourada" },
  { code: "312", name: "Hscm" },
  { code: "313", name: "Amazônia" },
  { code: "318", name: "Banco BMG" },
  { code: "319", name: "OM" },
  { code: "320", name: "China Construction Bank" },
  { code: "321", name: "Crefaz" },
  { code: "322", name: "Cooperativa de Crédito de Livre Admissão de Maringá" },
  { code: "323", name: "Mercado Pago" },
  { code: "324", name: "Cartos" },
  { code: "325", name: "Órama" },
  { code: "326", name: "Parati" },
  { code: "329", name: "QI SCD" },
  { code: "330", name: "Banco Bari" },
  { code: "331", name: "Fram Capital" },
  { code: "332", name: "Acesso" },
  { code: "335", name: "Banco Digio" },
  { code: "336", name: "C6 Bank" },
  { code: "340", name: "Super Pagamentos" },
  { code: "341", name: "Itaú Unibanco" },
  { code: "342", name: "Creditas" },
  { code: "343", name: "FFA" },
  { code: "348", name: "Banco XP" },
  { code: "349", name: "AL5" },
  { code: "350", name: "Cooperativa de Crédito Rural de Abelardo Luz" },
  { code: "352", name: "Toro" },
  { code: "354", name: "Necton Investimentos" },
  { code: "355", name: "Ótimo" },
  { code: "358", name: "Midway" },
  { code: "359", name: "Zema" },
  { code: "360", name: "Trinus Capital" },
  { code: "362", name: "Cielo" },
  { code: "363", name: "Socopa" },
  { code: "364", name: "Gerencianet" },
  { code: "365", name: "Simpaul" },
  { code: "366", name: "Banco Société Générale Brasil" },
  { code: "367", name: "Vitreo" },
  { code: "368", name: "Banco CSF" },
  { code: "370", name: "Banco Mizuho" },
  { code: "371", name: "Warren" },
  { code: "373", name: "UP Financeira" },
  { code: "374", name: "Realize" },
  { code: "376", name: "Banco J.P. Morgan" },
  { code: "377", name: "BMS" },
  { code: "378", name: "BBC Leasing" },
  { code: "379", name: "Cooperforte" },
  { code: "380", name: "PicPay" },
  { code: "381", name: "Banco Mercedes-Benz" },
  { code: "382", name: "Fiducia" },
  { code: "383", name: "Juno" },
  { code: "384", name: "Global SCM" },
  { code: "385", name: "Cooperativa de Economia e Crédito Mútuo dos Trabalhadores Portuários" },
  { code: "386", name: "Nu Financeira" },
  { code: "387", name: "Banco Toyota" },
  { code: "389", name: "Banco Mercantil do Brasil" },
  { code: "390", name: "Banco GM" },
  { code: "391", name: "Cooperativa de Crédito Rural de Ibiam" },
  { code: "393", name: "Banco Volkswagen" },
  { code: "394", name: "Banco Bradesco Financiamentos" },
  { code: "395", name: "F.D'Gold" },
  { code: "396", name: "Hub Pagamentos" },
  { code: "397", name: "Listo" },
  { code: "398", name: "Ideal" },
  { code: "399", name: "Kirton Bank" },
  { code: "400", name: "Cooperativa de Crédito, Poupança e Serviços Financeiros do Centro Oeste" },
  { code: "401", name: "Iugu" },
  { code: "402", name: "Cobuccio" },
  { code: "403", name: "Cora" },
  { code: "404", name: "Sumup" },
  { code: "406", name: "Accredito" },
  { code: "407", name: "Sefer" },
  { code: "408", name: "Bônuscred" },
  { code: "410", name: "Planner Trustee" },
  { code: "411", name: "Via Certa Financiadora" },
  { code: "412", name: "Banco Capital" },
  { code: "413", name: "Banco BV" },
  { code: "414", name: "Work" },
  { code: "416", name: "Lamara" },
  { code: "418", name: "Zipdin" },
  { code: "419", name: "Numbrs" },
  { code: "420", name: "CC LAR Credi" },
  { code: "421", name: "LAR Cooperativa de Crédito" },
  { code: "422", name: "Banco Safra" },
  { code: "423", name: "Coluna" },
  { code: "425", name: "Toro" },
  { code: "426", name: "Biorc" },
  { code: "427", name: "Cooperativa de Crédito Credicitrus" },
  { code: "428", name: "Cred-System" },
  { code: "429", name: "Crediare" },
  { code: "430", name: "Cooperativa de Crédito Rural Seara" },
  { code: "433", name: "BR-Capital" },
  { code: "435", name: "Delcred" },
  { code: "438", name: "Planner" },
  { code: "439", name: "ID" },
  { code: "440", name: "Credibrf" },
  { code: "442", name: "Magnetis" },
  { code: "443", name: "Credihome" },
  { code: "444", name: "Trinus SCD" },
  { code: "445", name: "Plantae" },
  { code: "447", name: "Mirae Asset" },
  { code: "448", name: "Hemera" },
  { code: "449", name: "DM" },
  { code: "450", name: "Fitbank" },
  { code: "451", name: "J17" },
  { code: "452", name: "Credifit" },
  { code: "454", name: "Mérito" },
  { code: "455", name: "Fênix" },
  { code: "456", name: "Banco MUFG Brasil" },
  { code: "457", name: "UY3" },
  { code: "458", name: "Hedge Investments" },
  { code: "459", name: "CCM Serv. Públicos SP" },
  { code: "460", name: "Asaas" },
  { code: "461", name: "Asaas IP" },
  { code: "462", name: "Stark Bank" },
  { code: "463", name: "Azumi" },
  { code: "464", name: "Banco Sumitomo Mitsui Brasileiro" },
  { code: "465", name: "Capital Consig" },
  { code: "467", name: "Master" },
  { code: "468", name: "Portoseg" },
  { code: "469", name: "Picpay Bank" },
  { code: "470", name: "CDC" },
  { code: "471", name: "Cecm Fabric Calçados Sapiranga" },
  { code: "473", name: "Banco Caixa Geral - Brasil" },
  { code: "475", name: "Banco Yamaha Motor" },
  { code: "477", name: "Citibank" },
  { code: "479", name: "Banco ItauBank" },
  { code: "487", name: "Deutsche Bank" },
  { code: "488", name: "JPMorgan Chase Bank" },
  { code: "492", name: "ING Bank" },
  { code: "495", name: "Banco de La Provincia de Buenos Aires" },
  { code: "505", name: "Banco Credit Suisse" },
  { code: "545", name: "Senso" },
  { code: "600", name: "Banco Luso Brasileiro" },
  { code: "604", name: "Banco Industrial do Brasil" },
  { code: "610", name: "Banco VR" },
  { code: "611", name: "Banco Paulista" },
  { code: "612", name: "Banco Guanabara" },
  { code: "613", name: "Omni Banco" },
  { code: "623", name: "Banco Pan" },
  { code: "626", name: "Banco C6 Consignado" },
  { code: "630", name: "Banco Smartbank" },
  { code: "633", name: "Banco Rendimento" },
  { code: "634", name: "Banco Triângulo" },
  { code: "637", name: "Banco Sofisa" },
  { code: "643", name: "Banco Pine" },
  { code: "652", name: "Itaú Unibanco Holding" },
  { code: "653", name: "Banco Indusval" },
  { code: "654", name: "Banco Digimais" },
  { code: "655", name: "Banco Votorantim" },
  { code: "707", name: "Banco Daycoval" },
  { code: "712", name: "Banco Ourinvest" },
  { code: "739", name: "Banco Cetelem" },
  { code: "741", name: "Banco Ribeirão Preto" },
  { code: "743", name: "Banco Semear" },
  { code: "745", name: "Banco Citibank" },
  { code: "746", name: "Banco Modal" },
  { code: "747", name: "Banco Rabobank International Brasil" },
  { code: "748", name: "Sicredi" },
  { code: "751", name: "Scotiabank Brasil" },
  { code: "752", name: "Banco BNP Paribas Brasil" },
  { code: "753", name: "Novo Banco Continental" },
  { code: "754", name: "Banco Sistema" },
  { code: "755", name: "Bank of America Merrill Lynch" },
  { code: "756", name: "Sicoob" },
  { code: "757", name: "Banco KEB Hana" },
].sort((a, b) => a.name.localeCompare(b.name));

export function CompanyBankAccountsPage() {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<BankAccountForm>({
    bankCode: "",
    bankName: "",
    agency: "",
    accountNumber: "",
    accountDigit: "",
    accountType: "CHECKING",
    holderName: "",
    holderDocument: "",
    pixKey: "",
    pixKeyType: "",
  });
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState("");

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: "default" | "danger";
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirmar",
    variant: "default",
    onConfirm: () => {},
    loading: false,
  });

  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
  }>({
    open: false,
    variant: "error",
    title: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [accountsData, infoData] = await Promise.all([
        companyApi.dashboard.listBankAccounts(),
        companyApi.getInfo(),
      ]);
      setAccounts(accountsData);
      setCompanyInfo(infoData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleBankSelect(code: string) {
    const bank = BANK_LIST.find((b) => b.code === code);
    setCreateForm((prev) => ({
      ...prev,
      bankCode: code,
      bankName: bank?.name || "",
    }));
  }

  function openCreateModal() {
    // Preenche automaticamente com dados da empresa
    const cnpjFormatado = companyInfo?.cnpj ? formatCnpj(companyInfo.cnpj) : "";
    setCreateForm({
      bankCode: "",
      bankName: "",
      agency: "",
      accountNumber: "",
      accountDigit: "",
      accountType: "CHECKING",
      holderName: companyInfo?.razaoSocial || "",
      holderDocument: cnpjFormatado,
      pixKey: cnpjFormatado,
      pixKeyType: "cnpj",
    });
    setFormError("");
    setShowCreateModal(true);
  }

  async function handleCreate() {
    if (
      !createForm.bankCode ||
      !createForm.agency ||
      !createForm.accountNumber ||
      !createForm.holderName ||
      !createForm.holderDocument
    ) {
      setFormError("Preencha todos os campos obrigatórios");
      return;
    }

    // Validar chave PIX obrigatória
    if (!createForm.pixKeyType || !createForm.pixKey) {
      setFormError("Chave PIX é obrigatória para receber saques");
      return;
    }

    // Validar formato básico da chave PIX
    const pixValidation = validatePixKeyFormat(createForm.pixKey, createForm.pixKeyType);
    if (!pixValidation.valid) {
      setFormError(pixValidation.error || "Formato de chave PIX inválido");
      return;
    }

    setCreating(true);
    setFormError("");

    try {
      await companyApi.dashboard.createBankAccount(createForm);
      await loadData();
      setShowCreateModal(false);
    } catch (error: any) {
      setFormError(error.message || "Erro ao criar conta");
    } finally {
      setCreating(false);
    }
  }

  function validatePixKeyFormat(key: string, type: string): { valid: boolean; error?: string } {
    if (type !== "cnpj") {
      return { valid: false, error: "Apenas chave PIX do tipo CNPJ é aceita" };
    }

    const cleanKey = key.replace(/\D/g, "");
    if (!/^\d{14}$/.test(cleanKey)) {
      return { valid: false, error: "CNPJ deve ter 14 dígitos" };
    }

    // Validar que é o mesmo CNPJ da empresa
    const companyCnpj = companyInfo?.cnpj?.replace(/\D/g, "") || "";
    if (cleanKey !== companyCnpj) {
      return { valid: false, error: "A chave PIX CNPJ deve ser o mesmo CNPJ da empresa" };
    }

    return { valid: true };
  }

  function handleDelete(id: string) {
    setConfirmDialog({
      open: true,
      title: "Excluir Conta",
      description:
        "Tem certeza que deseja excluir esta conta bancária? Esta ação não pode ser desfeita.",
      confirmText: "Excluir",
      variant: "danger",
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await companyApi.dashboard.deleteBankAccount(id);
          await loadData();
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        } catch (error: any) {
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: "error",
            title: "Erro ao excluir conta",
            description: error.message || "Não foi possível excluir a conta bancária. Tente novamente.",
          });
        }
      },
    });
  }

  const formatCnpj = (cnpj: string) => {
    const cleaned = cnpj.replace(/\D/g, "");
    if (cleaned.length === 14) {
      return cleaned.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5"
      );
    }
    return cnpj;
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Landmark className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Contas Bancárias"
        description="Gerencie suas contas para recebimento de saques"
        stats={[
          { value: accounts.length, label: "Total" },
          { value: accounts.filter((a) => a.isVerified).length, label: "Verificadas" },
        ]}
        actions={
          <Button
            onClick={openCreateModal}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Conta
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Carregando...
            </div>
          ) : accounts.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Landmark className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="mb-2">Nenhuma conta cadastrada</p>
              <p className="text-sm">
                Cadastre uma conta bancária para receber seus saques.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-border">
                    <tr className="text-left text-sm text-muted-foreground">
                      <th className="p-4 font-medium">Banco</th>
                      <th className="p-4 font-medium">Agência/Conta</th>
                      <th className="p-4 font-medium">Chave PIX</th>
                      <th className="p-4 font-medium">Titular</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr
                        key={account.id}
                        className="border-b border-border last:border-0 hover:bg-muted/50"
                      >
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{account.bankName}</p>
                            <p className="text-sm text-muted-foreground">
                              {account.bankCode}
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium">
                              Ag. {account.agency} | CC {account.accountNumber}
                              {account.accountDigit && `-${account.accountDigit}`}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {account.accountType === "CHECKING"
                                ? "Conta Corrente"
                                : "Poupança"}
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          {account.pixKey ? (
                            <div>
                              <p className="font-medium text-sm truncate max-w-[180px]" title={account.pixKey}>
                                {account.pixKey}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {getPixKeyTypeLabel(account.pixKeyType || "")}
                              </p>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{account.holderName}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatCnpj(account.holderDocument)}
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          {account.isVerified ? (
                            <Badge variant="success" className="flex items-center gap-1 w-fit">
                              <CheckCircle className="w-3 h-3" />
                              Verificada
                            </Badge>
                          ) : (
                            <Badge variant="warning" className="flex items-center gap-1 w-fit">
                              <Clock className="w-3 h-3" />
                              Pendente
                            </Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[120px]">
                              <DropdownMenuItem
                                onClick={() => handleDelete(account.id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-3 h-3 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-border">
                {accounts.map((account) => (
                  <div key={account.id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{account.bankName}</p>
                        <p className="text-sm text-muted-foreground">{account.bankCode}</p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[120px]">
                          <DropdownMenuItem
                            onClick={() => handleDelete(account.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-3 h-3 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="text-sm">
                      <p className="font-medium">
                        Ag. {account.agency} | CC {account.accountNumber}
                        {account.accountDigit && `-${account.accountDigit}`}
                      </p>
                      <p className="text-muted-foreground">
                        {account.accountType === "CHECKING" ? "Conta Corrente" : "Poupança"}
                      </p>
                    </div>
                    {account.pixKey && (
                      <div className="text-sm bg-muted/50 rounded px-2 py-1">
                        <span className="text-muted-foreground">PIX: </span>
                        <span className="font-medium truncate">{account.pixKey}</span>
                        <span className="text-xs text-muted-foreground ml-1">
                          ({getPixKeyTypeLabel(account.pixKeyType || "")})
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        <p className="font-medium truncate max-w-[200px]">{account.holderName}</p>
                        <p className="text-muted-foreground">{formatCnpj(account.holderDocument)}</p>
                      </div>
                      {account.isVerified ? (
                        <Badge variant="success" className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Verificada
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Pendente
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between pb-4 flex-shrink-0">
              <CardTitle className="text-lg">Nova Conta Bancária</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4 overflow-y-auto flex-1">
              <p className="text-sm text-muted-foreground">
                A conta deve ser da mesma titularidade da empresa (mesmo CNPJ).
              </p>

              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              {/* Layout em duas colunas no desktop, única coluna no mobile */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Coluna Esquerda - Dados Bancários */}
                <div className="space-y-3 sm:space-y-4">
                  <div className="space-y-2">
                    <Label>Banco *</Label>
                    <SearchableSelect
                      options={BANK_LIST.map((bank) => ({
                        value: bank.code,
                        label: `${bank.code} - ${bank.name}`,
                      }))}
                      value={createForm.bankCode}
                      onChange={handleBankSelect}
                      placeholder="Selecione o banco"
                      searchPlaceholder="Buscar banco..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="space-y-2">
                      <Label>Agência *</Label>
                      <Input
                        placeholder="0000"
                        value={createForm.agency}
                        onChange={(e) =>
                          setCreateForm({ ...createForm, agency: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo de Conta *</Label>
                      <select
                        value={createForm.accountType}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            accountType: e.target.value as "CHECKING" | "SAVINGS",
                          })
                        }
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="CHECKING">Conta Corrente</option>
                        <option value="SAVINGS">Poupança</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div className="col-span-2 space-y-2">
                      <Label>Número da Conta *</Label>
                      <Input
                        placeholder="00000000"
                        value={createForm.accountNumber}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            accountNumber: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Dígito</Label>
                      <Input
                        placeholder="0"
                        value={createForm.accountDigit}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            accountDigit: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Nome do Titular</Label>
                    <Input
                      value={createForm.holderName}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      Preenchido automaticamente com a razão social da empresa
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>CNPJ do Titular</Label>
                    <Input
                      value={createForm.holderDocument}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      Preenchido automaticamente com o CNPJ da empresa
                    </p>
                  </div>
                </div>

                {/* Coluna Direita - Chave PIX */}
                <div className="space-y-3 sm:space-y-4 border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-6">
                  <div>
                    <h4 className="font-medium mb-1 sm:mb-2">Chave PIX para Recebimento</h4>
                    <p className="text-sm text-muted-foreground">
                      Apenas chaves PIX do tipo CNPJ são aceitas. O CNPJ da chave PIX deve ser o mesmo da empresa.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Tipo de Chave PIX</Label>
                    <Input
                      value="CNPJ"
                      disabled
                      className="bg-muted"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Chave PIX (CNPJ)</Label>
                    <Input
                      value={createForm.pixKey}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      Preenchido automaticamente com o CNPJ da empresa
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="w-full sm:w-auto"
                >
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={creating} className="w-full sm:w-auto">
                  {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Cadastrar Conta
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
      />

      {/* Result Dialog */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
      />
    </div>
  );
}
